/**
 * utils/ExcelDataPreparation.js
 * Service de préparation des données pour l'enrichissement Excel
 */

export class ExcelDataPreparation {
  constructor(state) {
    this.state = state;
    this.sources = state.getAllSources();

    // Validation des sources requises
    this.validateSources();
  }

  /**
   * Valider que toutes les sources nécessaires sont disponibles
   */
  validateSources() {
    const requiredSources = ["baseROP", "configTiroirs"];
    const missingSources = requiredSources.filter(
      (source) => !this.sources[source] || this.sources[source].status !== "success"
    );

    if (missingSources.length > 0) {
      throw new Error(`Sources manquantes pour Excel: ${missingSources.join(", ")}`);
    }
  }

  /**
   * Préparer toutes les fibres pour l'export Excel
   */
  prepareAllFibers() {
    const baseRop = this.sources.baseROP.data.references;

    const results = baseRop.map((fiber, index) => {
      if (index % 100 === 0) {
      }
      return this.buildFiberData(fiber);
    });

    return results;
  }

  /**
   * Construire les données d'une fibre pour Excel
   */
  buildFiberData(fiber) {
    // 1. Données de base depuis baseROP
    const result = {
      REFERENCE: fiber.REFERENCE,
      PBO: fiber.PBO_VALUE,
      PORT: fiber.PORT, // Le PORT vient directement de baseROP
    };

    // 2. Trouver la configuration correspondante
    const configMatch = this.findConfigMatch(fiber);
    if (!configMatch) {
      return {
        ...result,
        STATUS: "ERROR",
        DISTANCE: "-",
        COMMENT: "Configuration non trouvée",
        TYPE: "ERROR",
      };
    }

    // 3. Appliquer la logique selon l'état
    if (configMatch.ETAT === "occupee") {
      const occupeeComment = this.findOccupeeComment(configMatch.REFERENCE_SHORT, fiber.PORT);

      return {
        ...result,
        STATUS: "Occupée",
        DISTANCE: "-",
        COMMENT: occupeeComment,
        TYPE: null,
      };
    } else if (configMatch.ETAT === "libre") {
      return this.processLibreFiber(result, configMatch, fiber.PORT);
    } else {
      return {
        ...result,
        STATUS: "ERROR",
        DISTANCE: "-",
        COMMENT: `État inconnu: ${configMatch.ETAT}`,
        TYPE: "ERROR",
      };
    }
  }

  /**
   * Trouver la configuration correspondante dans configTiroirs
   */
  findConfigMatch(fiber) {
    const configTiroirs = this.sources.configTiroirs.data.references;
    // Match sur REFERENCE_LONG ET PORT
    return configTiroirs.find((config) => config.REFERENCE_LONG === fiber.REFERENCE && config.PORT === fiber.PORT);
  }

  /**
   * Traiter une fibre libre (recherche dans pvReussite/pvEchec/pvHs)
   */
  processLibreFiber(baseResult, configMatch, port) {
    const shortRef = configMatch.REFERENCE_SHORT;

    // 1. Chercher dans rapportFiberPvReussite
    const reussiteMatch = this.findInRapportReussite(shortRef, port);
    if (reussiteMatch) {
      return {
        ...baseResult,
        STATUS: "OK",
        DISTANCE: reussiteMatch.DISTANCE_AVERAGE,
        COMMENT: "Mesure effectuée avec succès",
        TYPE: "SUCCES",
      };
    }

    // 2. Chercher dans rapportFiberPvEchec
    const echecMatch = this.findInRapportEchec(shortRef, port);
    if (echecMatch) {
      return {
        ...baseResult,
        STATUS: "OK",
        DISTANCE: echecMatch.DISTANCE_AVERAGE,
        COMMENT: "Mesure effectuée avec un PV d'échec",
        TYPE: "ECHEC",
      };
    }

    // 3. Chercher dans pvHs
    const hsMatch = this.findInPvHs(shortRef, port);
    if (hsMatch) {
      const hsComment = this.findHsComment(shortRef, port);
      return {
        ...baseResult,
        STATUS: "HS",
        DISTANCE: "-",
        COMMENT: hsComment,
        TYPE: "HS",
      };
    }

    // 4. Aucune mesure trouvée
    return {
      ...baseResult,
      STATUS: "NON CONFIGURÉE",
      DISTANCE: "-",
      COMMENT: "Aucune mesure trouvée",
      TYPE: "NO_DATA",
    };
  }

  /**
   * Chercher dans rapportFiberPvReussite
   */
  findInRapportReussite(shortRef, port) {
    const rapportReussite = this.sources.rapportFiberPvReussite?.data?.references;
    if (!rapportReussite) return null;

    return rapportReussite.find((rapport) => rapport.REFERENCE === shortRef && String(rapport.PORT) === String(port));
  }

  /**
   * Chercher dans rapportFiberPvEchec
   */
  findInRapportEchec(shortRef, port) {
    const rapportEchec = this.sources.rapportFiberPvEchec?.data?.references;
    if (!rapportEchec) return null;

    return rapportEchec.find((rapport) => rapport.REFERENCE === shortRef && String(rapport.PORT) === String(port));
  }

  /**
   * Chercher dans pvHs
   */
  findInPvHs(shortRef, port) {
    const pvHs = this.sources.pvHs?.data?.fibers;
    if (!pvHs) return null;

    return pvHs.find(
      (hs) => hs.REFERENCE === shortRef && String(hs.port) === String(port) // "port" en minuscule
    );
  }

  /**
   * Récupérer le commentaire personnalisé pour une fibre HS
   */
  findHsComment(shortRef, port) {
    const conflictResolutions = this.sources.conflictResolutions?.data?.fibers;
    if (!conflictResolutions) {
      return "Fibre hors service - localisation à préciser";
    }

    const conflictMatch = conflictResolutions.find(
      (conflict) => conflict.REFERENCE === shortRef && String(conflict.PORT) === String(port) // Conversion en string pour le match
    );

    return conflictMatch?.COMMENT || "Fibre hors service - localisation à préciser";
  }

  /**
   * Récupérer le commentaire personnalisé pour une fibre occupée
   */
  findOccupeeComment(shortRef, port) {
    const conflictResolutions = this.sources.conflictResolutions?.data?.fibers;
    if (!conflictResolutions) {
      return "Position Occupée";
    }

    const conflictMatch = conflictResolutions.find(
      (conflict) =>
        conflict.REFERENCE === shortRef && String(conflict.PORT) === String(port) && conflict.ETAT === "occupee" // Filtrer sur les occupées
    );

    return conflictMatch?.COMMENT || "Position Occupée";
  }

  /**
   * Export des données préparées
   */
  export() {
    const preparedData = this.prepareAllFibers();

    return {
      data: preparedData,
      timestamp: new Date().toISOString(),
    };
  }
}
